/**
 * ServiceTitan Photo Import — temp-worker page.
 *
 * Single-page workflow:
 *   1. Show worker the next site (queue claim is server-driven).
 *   2. Worker copies the address, opens ServiceTitan, downloads the
 *      photos as a ZIP, drops it on the page.
 *   3. Server unzips + auto-routes images to matching assets vs the
 *      site bucket; thumbnails come back so the worker can confirm.
 *   4. Worker hits Done & Next → server completes the queue row and
 *      hands back the next claim in the same response.
 *
 * Skip behavior (no longer terminal): a "Skip" pushes the site to the
 * back of the queue. The second time the same site comes around, the
 * page asks "are there pictures here?" — a "no" sets status=no_photos
 * and removes the site from the docket entirely.
 *
 * Safeguards (server-enforced, surfaced here):
 *   - 413 FILE_TOO_LARGE                       → friendly cap message
 *   - 400 ZIP_HAS_NO_IMAGES                    → tell them to use Skip
 *   - 409 ZIP_ALREADY_UPLOADED_TO_DIFFERENT_SITE → hard block, show prior site
 *   - 409 CROSS_SITE_SERIAL_MATCH              → confirm dialog; on yes,
 *                                                re-upload with ack flag
 *
 * Lease: server auto-releases after LEASE_MINUTES if the worker walks
 * away. We surface a countdown so they can hit Done before it expires.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    Camera,
    Copy,
    ExternalLink,
    LogOut,
    Image as ImageIcon,
    SkipForward,
    Upload,
    CheckCircle2,
    Loader2,
    AlertTriangle,
    MapPin,
    XCircle,
    RotateCcw,
    X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import {
    api,
    ApiError,
    getToken,
} from "@/lib/api";
import { openExternalLink } from "@/lib/standalone";
import { cn } from "@/lib/utils";

const MAX_ZIP_BYTES = 1024 * 1024 * 1024;

interface KnownAsset {
    id: string;
    name: string | null;
    serialNumber: string | null;
    manufacturer: string | null;
    model: string | null;
}

interface Claim {
    siteId: string;
    customerName: string | null;
    address: string | null;
    address2: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    stExternalKey: string | null;
    skipCount: number;
    knownAssets: KnownAsset[];
    claimedAt: string;
    leaseExpiresAt: string;
}

interface NextResponse {
    claim: Claim | null;
    queueEmpty: boolean;
}

interface QueueSummary {
    total: number;
    pending: number;
    claimed: number;
    done: number;
    noPhotos: number;
    mineClaimed: number;
    doneToday: number;
}

interface UploadedAttachment {
    id: string;
    fileName: string;
    attachedTo: "asset" | "site";
    assetId: string | null;
    previewUrl: string;
}

interface UploadResponse {
    uploaded: number;
    matchedToAsset: number;
    attachedToSite: number;
    skippedDuplicate: number;
    skippedNonImage: number;
    skippedTooLarge: number;
    skippedOverflow: number;
    attachments: UploadedAttachment[];
}

interface CompleteResponse {
    ok: boolean;
    claim: Claim | null;
    queueEmpty: boolean;
}

interface ReuploadRow {
    siteId: string;
    addressLine: string | null;
    addressLine2: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    customerName: string | null;
    queueStatus: string;
    uploadedAt: string | null;
    priorPhotoCount: number;
    lastJobCompletedAt: string | null;
    lastJobStNumber: string | null;
    completedJobsSinceUpload: number;
}

interface ReuploadList {
    total: number;
    limit: number;
    offset: number;
    rows: ReuploadRow[];
}

interface ReclaimResponse {
    ok: boolean;
    claim: Claim | null;
    isReupload: boolean;
}

interface CrossSiteHit {
    serialNumber: string;
    siteAddress: string;
}

type CrossSiteWarning = { hits: CrossSiteHit[]; pendingZip: File } | null;
type ZeroDoneConfirm = { open: boolean };

function formatAddress(c: Claim): string {
    const head = [c.address, c.address2].filter(Boolean).join(", ");
    const tail = [c.city, c.region, c.postalCode].filter(Boolean).join(", ");
    return [head, tail].filter(Boolean).join(", ");
}

function bytesHuman(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * Open ServiceTitan in a new tab pre-filled with this site's address.
 * Real ST URLs depend on the tenant + customer record, so we open the
 * generic "search" surface and leave the worker one paste away.
 */
function openServiceTitanSearch(address: string | null): void {
    const q = encodeURIComponent(address ?? "");
    openExternalLink(`https://go.servicetitan.com/#/Search?term=${q}`);
}

export default function PhotoImportPage() {
    const auth = useAuth();
    const qc = useQueryClient();

    const [claim, setClaim] = useState<Claim | null>(null);
    const [queueEmpty, setQueueEmpty] = useState(false);
    const [loadingNext, setLoadingNext] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [recentUploads, setRecentUploads] = useState<UploadedAttachment[]>([]);
    const [uploadStats, setUploadStats] = useState<UploadResponse | null>(null);

    // True after at least one successful upload for the CURRENT claim.
    // Resets every time the claim changes. Powers the "Done & Next
    // with 0 photos" confirm dialog.
    const [hasUploadedThisClaim, setHasUploadedThisClaim] = useState(false);

    // Skip-second-pass UI: when claim.skipCount >= 1, clicking Skip
    // opens this confirm panel instead of immediately skipping.
    const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);

    // Done & Next zero-photo confirm panel.
    const [zeroDoneConfirm, setZeroDoneConfirm] = useState<ZeroDoneConfirm>({ open: false });

    // Cross-site serial warning. Holds the hits + the pending zip File
    // so we can re-upload with the ack flag if the worker confirms.
    const [crossSiteWarning, setCrossSiteWarning] = useState<CrossSiteWarning>(null);

    const [now, setNow] = useState(Date.now());
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [dragActive, setDragActive] = useState(false);

    // Re-upload bucket: sites where Reegen already finished a pass but
    // a new ServiceTitan job has been completed since, so there are
    // likely new after-photos to bring across. Modal-driven so the
    // main queue flow stays uncluttered when nothing is due.
    const [reuploadOpen, setReuploadOpen] = useState(false);
    const [reuploading, setReuploading] = useState<string | null>(null);

    // Countdown ticker for the lease label.
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const summary = useQuery<QueueSummary>({
        queryKey: ["photo-import", "summary"],
        queryFn: () => api.get<QueueSummary>("/api/photo-import/queue/summary"),
        refetchInterval: 15_000,
    });

    // Re-upload list. Polls quietly so the header badge stays accurate
    // as new ST jobs land. Cheap query — under 100 rows for a healthy
    // tenant and only refetched every 60s.
    const reuploadQuery = useQuery<ReuploadList>({
        queryKey: ["photo-import", "re-upload"],
        queryFn: () =>
            api.get<ReuploadList>("/api/photo-import/queue/re-upload?limit=200"),
        refetchInterval: 60_000,
    });

    const fetchNext = useCallback(async () => {
        setLoadingNext(true);
        try {
            const data = await api.post<NextResponse>("/api/photo-import/queue/next");
            setClaim(data.claim);
            setQueueEmpty(data.queueEmpty);
            setRecentUploads([]);
            setUploadStats(null);
            setHasUploadedThisClaim(false);
            setSkipConfirmOpen(false);
            setZeroDoneConfirm({ open: false });
            setCrossSiteWarning(null);
        } catch (err) {
            toast.error("Couldn't get the next site. Try Refresh.");
            console.error(err);
        } finally {
            setLoadingNext(false);
        }
    }, []);

    useEffect(() => {
        void fetchNext();
    }, [fetchNext]);

    // Best-effort release on tab close so we don't strand claims.
    useEffect(() => {
        const handler = () => {
            if (!claim) return;
            const token = getToken();
            if (!token) return;
            const url = `/api/photo-import/queue/${claim.siteId}/release`;
            try {
                navigator.sendBeacon(
                    url,
                    new Blob([JSON.stringify({})], { type: "application/json" }),
                );
            } catch {
                /* ignore — server will time out the lease */
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [claim]);

    /**
     * Parse an ApiError body that might come back as a string or an
     * already-parsed JSON object. Returns the typed shape if it looks
     * like one of our error envelopes, else null.
     */
    function asErrorBody(err: unknown): { error?: string; message?: string; [k: string]: unknown } | null {
        if (!(err instanceof ApiError)) return null;
        const body = err.body;
        if (body && typeof body === "object") return body as Record<string, unknown>;
        return null;
    }

    /**
     * Core upload routine. Pulled out as its own function so the
     * cross-site warning's "Yes, upload anyway" path can call it
     * again with `acknowledge: true` after the worker confirms.
     */
    const performUpload = useCallback(
        async (zip: File, options: { acknowledgeCrossSiteMatch?: boolean } = {}) => {
            if (!claim) return;
            setUploading(true);
            try {
                const fd = new FormData();
                fd.append("file", zip);
                if (options.acknowledgeCrossSiteMatch) {
                    fd.append("acknowledgeCrossSiteMatch", "true");
                }
                const data = await api.postForm<UploadResponse>(
                    `/api/photo-import/queue/${claim.siteId}/upload`,
                    fd,
                );
                setRecentUploads((prev) => [...data.attachments, ...prev]);
                setUploadStats((prev) => ({
                    uploaded: (prev?.uploaded ?? 0) + data.uploaded,
                    matchedToAsset: (prev?.matchedToAsset ?? 0) + data.matchedToAsset,
                    attachedToSite: (prev?.attachedToSite ?? 0) + data.attachedToSite,
                    skippedDuplicate: (prev?.skippedDuplicate ?? 0) + data.skippedDuplicate,
                    skippedNonImage: (prev?.skippedNonImage ?? 0) + data.skippedNonImage,
                    skippedTooLarge: (prev?.skippedTooLarge ?? 0) + data.skippedTooLarge,
                    skippedOverflow: (prev?.skippedOverflow ?? 0) + data.skippedOverflow,
                    attachments: [...data.attachments, ...(prev?.attachments ?? [])],
                }));
                if (data.uploaded > 0) setHasUploadedThisClaim(true);
                if (data.uploaded === 0 && data.skippedDuplicate > 0) {
                    toast.info(
                        `Already imported · ${data.skippedDuplicate} duplicate${
                            data.skippedDuplicate === 1 ? "" : "s"
                        } skipped`,
                    );
                } else {
                    toast.success(
                        `Uploaded ${data.uploaded} photo${data.uploaded === 1 ? "" : "s"}` +
                            (data.matchedToAsset > 0
                                ? ` · ${data.matchedToAsset} auto-matched to a device`
                                : "") +
                            (data.skippedDuplicate > 0
                                ? ` · ${data.skippedDuplicate} duplicate${
                                      data.skippedDuplicate === 1 ? "" : "s"
                                  } skipped`
                                : ""),
                    );
                }
            } catch (err) {
                const body = asErrorBody(err);
                const status = err instanceof ApiError ? err.status : 0;

                // 413 — file too large.
                if (status === 413 || body?.error === "FILE_TOO_LARGE") {
                    const sizeStr = bytesHuman(zip.size);
                    toast.error(`That ZIP is ${sizeStr} — limit is 1 GB. Try splitting the photos.`);
                    return;
                }

                // 409 — same ZIP already attached to a different site.
                // HARD reject; show the prior site so the worker
                // realizes which file they grabbed.
                if (status === 409 && body?.error === "ZIP_ALREADY_UPLOADED_TO_DIFFERENT_SITE") {
                    const prior = (body.priorAddress as string | undefined) ?? "another site";
                    toast.error(
                        `This ZIP was already uploaded to ${prior}. Make sure you're on the right address before continuing.`,
                        { duration: 12_000 },
                    );
                    return;
                }

                // 409 — filenames look like a different site. Open the
                // confirm panel so the worker can decide explicitly.
                if (status === 409 && body?.error === "CROSS_SITE_SERIAL_MATCH") {
                    const hits = (body.hits as CrossSiteHit[] | undefined) ?? [];
                    setCrossSiteWarning({ hits, pendingZip: zip });
                    return;
                }

                // 400 — empty ZIP.
                if (status === 400 && body?.error === "ZIP_HAS_NO_IMAGES") {
                    toast.error("This ZIP has no photos in it. Use Skip if you didn't get to look yet.");
                    return;
                }

                const msg = (body?.message as string | undefined) ?? "Upload failed. Check the file and try again.";
                toast.error(msg);
                console.error("[photo-import] upload error", err);
            } finally {
                setUploading(false);
            }
        },
        [claim],
    );

    const handleFiles = useCallback(
        async (files: FileList | File[]) => {
            if (!claim) return;
            const list = Array.from(files);
            const zip = list.find((f) => f.name.toLowerCase().endsWith(".zip"));
            if (!zip) {
                toast.error("Drop the ZIP file ServiceTitan downloaded — not loose images.");
                return;
            }
            // Client-side pre-check so a 1 GB+ ZIP never even hits
            // the network — saves a long upload that's just going to
            // 413.
            if (zip.size > MAX_ZIP_BYTES) {
                toast.error(`That ZIP is ${bytesHuman(zip.size)} — limit is 1 GB. Try splitting the photos.`);
                return;
            }
            await performUpload(zip);
        },
        [claim, performUpload],
    );

    const onDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragActive(false);
            if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files);
        },
        [handleFiles],
    );

    const completeNow = useCallback(async () => {
        if (!claim) return;
        try {
            const data = await api.post<CompleteResponse>(
                `/api/photo-import/queue/${claim.siteId}/complete`,
                {},
            );
            toast.success("Site marked done.");
            setClaim(data.claim);
            setQueueEmpty(data.queueEmpty);
            setRecentUploads([]);
            setUploadStats(null);
            setHasUploadedThisClaim(false);
            setZeroDoneConfirm({ open: false });
            void qc.invalidateQueries({ queryKey: ["photo-import", "summary"] });
        } catch {
            toast.error("Couldn't complete this site.");
        }
    }, [claim, qc]);

    const onClickDoneAndNext = useCallback(() => {
        if (!claim) return;
        // Safeguard: if the worker hits Done & Next without a single
        // successful upload for this claim, force them to confirm.
        // Sometimes legit (a site genuinely had nothing in ST), but
        // way more often it's "I forgot to drop the ZIP first".
        if (!hasUploadedThisClaim) {
            setZeroDoneConfirm({ open: true });
            return;
        }
        void completeNow();
    }, [claim, hasUploadedThisClaim, completeNow]);

    /** First-time skip: just push to back. */
    const skipPushBack = useCallback(async () => {
        if (!claim) return;
        try {
            const data = await api.post<CompleteResponse>(
                `/api/photo-import/queue/${claim.siteId}/skip`,
                {},
            );
            toast.success("Pushed to the back of the list. We'll come back to it later.");
            setClaim(data.claim);
            setQueueEmpty(data.queueEmpty);
            setRecentUploads([]);
            setUploadStats(null);
            setHasUploadedThisClaim(false);
            setSkipConfirmOpen(false);
            void qc.invalidateQueries({ queryKey: ["photo-import", "summary"] });
        } catch {
            toast.error("Couldn't skip this site.");
        }
    }, [claim, qc]);

    /** Terminal: confirmed no photos at this site, remove from docket. */
    const markNoPhotos = useCallback(async () => {
        if (!claim) return;
        try {
            const data = await api.post<CompleteResponse>(
                `/api/photo-import/queue/${claim.siteId}/no-photos`,
                {},
            );
            toast.success("Removed — no photos at this location.");
            setClaim(data.claim);
            setQueueEmpty(data.queueEmpty);
            setRecentUploads([]);
            setUploadStats(null);
            setHasUploadedThisClaim(false);
            setSkipConfirmOpen(false);
            void qc.invalidateQueries({ queryKey: ["photo-import", "summary"] });
        } catch {
            toast.error("Couldn't update this site.");
        }
    }, [claim, qc]);

    /**
     * Re-claim a re-upload-due site. Transitions the terminal queue
     * row back to claimed and adopts it as the current claim, so the
     * existing drop-zone and complete buttons take over. The server
     * tags the resulting `complete` event as `re_uploaded` so reports
     * can distinguish first-time uploads from re-uploads.
     */
    const reclaimReupload = useCallback(
        async (siteId: string) => {
            setReuploading(siteId);
            try {
                const data = await api.post<ReclaimResponse>(
                    `/api/photo-import/queue/${siteId}/reclaim-for-reupload`,
                );
                if (!data.claim) {
                    toast.error("Couldn't reclaim. Refresh and try again.");
                    return;
                }
                setClaim(data.claim);
                setQueueEmpty(false);
                setRecentUploads([]);
                setUploadStats(null);
                setHasUploadedThisClaim(false);
                setReuploadOpen(false);
                void qc.invalidateQueries({ queryKey: ["photo-import", "summary"] });
                void qc.invalidateQueries({ queryKey: ["photo-import", "re-upload"] });
                toast.success("Re-upload claimed. Drop the new ZIP — duplicates are auto-skipped.");
            } catch (err) {
                const status = err instanceof ApiError ? err.status : 0;
                if (status === 409) {
                    toast.error("Site was just reclaimed by someone else, or no longer needs a re-upload.");
                    void reuploadQuery.refetch();
                } else {
                    toast.error("Couldn't reclaim. Try again.");
                    console.error(err);
                }
            } finally {
                setReuploading(null);
            }
        },
        [qc, reuploadQuery],
    );

    /** Skip button entry: routes to either push-back or the confirm panel. */
    const onClickSkip = useCallback(() => {
        if (!claim) return;
        if (claim.skipCount >= 1) {
            // Second pass — ask "are there pictures here?"
            setSkipConfirmOpen(true);
        } else {
            void skipPushBack();
        }
    }, [claim, skipPushBack]);

    const copyAddress = useCallback(async () => {
        if (!claim?.address) return;
        try {
            await navigator.clipboard.writeText(formatAddress(claim));
            toast.success("Address copied.");
        } catch {
            toast.error("Copy failed — select and copy manually.");
        }
    }, [claim]);

    const leaseInfo = useMemo(() => {
        if (!claim) return null;
        const expiresAt = new Date(claim.leaseExpiresAt).getTime();
        const ms = Math.max(0, expiresAt - now);
        const min = Math.floor(ms / 60_000);
        const sec = Math.floor((ms % 60_000) / 1000);
        return {
            label: `${min}:${String(sec).padStart(2, "0")}`,
            expired: ms <= 0,
        };
    }, [claim, now]);

    const total = summary.data?.total ?? 0;
    const done = summary.data?.done ?? 0;
    const noPhotos = summary.data?.noPhotos ?? 0;
    const closed = done + noPhotos;
    const pct = total > 0 ? Math.round((closed / total) * 100) : 0;
    const reuploadCount = reuploadQuery.data?.total ?? 0;
    const reuploadRows = reuploadQuery.data?.rows ?? [];

    const claimAddressLabel = claim ? (claim.address ?? "(no address on file)") : "";

    return (
        <div className="max-w-4xl mx-auto py-6 space-y-6">
            {/* Header strip */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Camera className="h-6 w-6 text-primary" />
                        ServiceTitan Photo Import
                    </h1>
                    <p className="text-sm text-foreground/80">
                        Signed in as <span className="font-medium">{auth.user?.email ?? "—"}</span>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {reuploadCount > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReuploadOpen(true)}
                            className="border-amber-500/50 text-amber-700 hover:text-amber-800 hover:border-amber-500"
                        >
                            <RotateCcw className="h-4 w-4" />
                            Re-upload · {reuploadCount}
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={auth.logout}>
                        <LogOut className="h-4 w-4" />
                        Sign out
                    </Button>
                </div>
            </div>

            {/* Progress strip */}
            <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center justify-between text-sm">
                    <div>
                        <span className="font-semibold">{closed.toLocaleString()}</span>
                        <span className="text-foreground/70"> of {total.toLocaleString()} sites closed</span>
                        <span className="text-foreground/70"> · </span>
                        <span className="font-medium">{summary.data?.doneToday ?? 0} today</span>
                        {noPhotos > 0 && (
                            <>
                                <span className="text-foreground/70"> · </span>
                                <span className="text-foreground/70">{noPhotos.toLocaleString()} no-photos</span>
                            </>
                        )}
                    </div>
                    <div className="text-foreground/70">{pct}%</div>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden mt-2">
                    <div
                        className="h-full bg-primary transition-[width] duration-500"
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>

            {loadingNext && (
                <div className="rounded-xl border bg-card p-8 flex items-center justify-center text-foreground/70">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Getting the next site…
                </div>
            )}

            {!loadingNext && queueEmpty && !claim && (
                <div className="rounded-xl border bg-card p-12 text-center space-y-3">
                    <CheckCircle2 className="h-12 w-12 text-success mx-auto" />
                    <h2 className="text-xl font-semibold">All sites are done.</h2>
                    <p className="text-foreground/70">
                        Nothing left in the queue. Take a break, or refresh in case new sites were added.
                    </p>
                    <Button onClick={() => void fetchNext()}>Refresh</Button>
                </div>
            )}

            {claim && (
                <>
                    {/* Site card */}
                    <div className="rounded-xl border bg-card p-6 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                                {claim.customerName && (
                                    <div className="text-sm font-medium text-foreground/80 mb-1">
                                        {claim.customerName}
                                    </div>
                                )}
                                <h2 className="text-2xl font-bold leading-tight break-words">
                                    {claimAddressLabel}
                                </h2>
                                <div className="text-sm text-foreground/70 mt-1">
                                    {[claim.city, claim.region, claim.postalCode].filter(Boolean).join(", ") ||
                                        "—"}
                                </div>
                                {claim.skipCount >= 1 && (
                                    <div className="inline-flex items-center gap-1 mt-2 text-xs font-medium px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/30">
                                        <SkipForward className="h-3 w-3" />
                                        Skipped before · pass #{claim.skipCount + 1}
                                    </div>
                                )}
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-xs uppercase tracking-wider text-foreground/70">Lease</div>
                                <div
                                    className={cn(
                                        "text-lg font-mono font-semibold",
                                        leaseInfo?.expired ? "text-destructive" : "text-foreground",
                                    )}
                                >
                                    {leaseInfo?.label ?? "—"}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button onClick={copyAddress} variant="outline" size="sm">
                                <Copy className="h-4 w-4" />
                                Copy address
                            </Button>
                            <Button
                                onClick={() => openServiceTitanSearch(formatAddress(claim))}
                                variant="outline"
                                size="sm"
                            >
                                <ExternalLink className="h-4 w-4" />
                                Open ServiceTitan
                            </Button>
                        </div>

                        {claim.knownAssets.length > 0 && (
                            <div className="rounded-lg bg-secondary/40 px-3 py-2 text-xs text-foreground/80">
                                <span className="font-medium">Known devices at this site:</span>{" "}
                                {claim.knownAssets
                                    .map((a) => a.serialNumber || a.name || "?")
                                    .filter(Boolean)
                                    .join(" · ")}
                                <div className="mt-1 text-foreground/70">
                                    Filenames containing any of these serials are auto-tagged to the device.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Steps */}
                    <ol className="rounded-xl border bg-card p-5 space-y-2 text-sm list-decimal list-inside marker:font-semibold marker:text-foreground/60">
                        <li>Click <span className="font-semibold">Open ServiceTitan</span> above.</li>
                        <li>Search this address, open the location, click the <span className="font-semibold">Photos</span> tab.</li>
                        <li>Select all photos and download them as a ZIP.</li>
                        <li>Drag the downloaded ZIP into the box below.</li>
                        <li>When the thumbnails look right, click <span className="font-semibold">Done & Next</span>.</li>
                    </ol>

                    {/* Drop zone — address banner echoes the destination so
                        the worker sees exactly where the photos are going.
                        Big and obvious to make a wrong-site ZIP-drop hard. */}
                    <div
                        onDragEnter={(e) => {
                            e.preventDefault();
                            setDragActive(true);
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragActive(true);
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            setDragActive(false);
                        }}
                        onDrop={onDrop}
                        className={cn(
                            "rounded-xl border-2 border-dashed p-8 transition-colors",
                            dragActive
                                ? "border-primary bg-primary/5"
                                : "border-border bg-card hover:border-primary/50",
                            uploading && "opacity-60 pointer-events-none",
                        )}
                    >
                        <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 mb-4 flex items-center gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-primary shrink-0" />
                            <div className="min-w-0">
                                <span className="text-foreground/70">Uploading photos for:</span>{" "}
                                <span className="font-bold text-foreground break-words">
                                    {claimAddressLabel}
                                </span>
                            </div>
                        </div>
                        <div className="text-center">
                            <Upload className="h-10 w-10 mx-auto text-foreground/60 mb-3" />
                            <div className="text-lg font-semibold">
                                {uploading ? "Uploading…" : "Drag the ZIP here"}
                            </div>
                            <div className="text-sm text-foreground/70 mt-1">or</div>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="mt-3"
                                disabled={uploading}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Choose file
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".zip,application/zip,application/x-zip-compressed"
                                className="hidden"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        void handleFiles(e.target.files);
                                        e.target.value = "";
                                    }
                                }}
                            />
                            {uploading && (
                                <div className="mt-3 flex items-center justify-center text-sm text-foreground/70">
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Unzipping and saving photos…
                                </div>
                            )}
                            <div className="text-xs text-foreground/60 mt-3">
                                Limit: 1 GB ZIP. Photos only (jpg, png, heic, etc.).
                            </div>
                        </div>
                    </div>

                    {/* Cross-site serial warning */}
                    {crossSiteWarning && (
                        <div className="rounded-xl border-2 border-amber-500/60 bg-amber-500/10 p-5 space-y-3">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-semibold text-amber-900 dark:text-amber-200">
                                        Filenames look like they're for a different site.
                                    </div>
                                    <p className="text-sm text-foreground/80 mt-1">
                                        The ZIP you dropped contains filenames that match serial numbers from
                                        another site:
                                    </p>
                                    <ul className="text-sm mt-2 space-y-1">
                                        {crossSiteWarning.hits.slice(0, 5).map((h, i) => (
                                            <li key={i} className="text-foreground/80">
                                                <code className="font-mono text-xs bg-background px-1 py-0.5 rounded border">
                                                    {h.serialNumber}
                                                </code>{" "}
                                                belongs to <span className="font-medium">{h.siteAddress}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="text-sm text-foreground/80 mt-2">
                                        Are you sure these photos are for{" "}
                                        <span className="font-bold">{claimAddressLabel}</span>?
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button variant="ghost" onClick={() => setCrossSiteWarning(null)}>
                                    No, cancel
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={() => {
                                        const zip = crossSiteWarning.pendingZip;
                                        setCrossSiteWarning(null);
                                        void performUpload(zip, { acknowledgeCrossSiteMatch: true });
                                    }}
                                >
                                    Yes, upload anyway
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Upload result summary + thumbnails */}
                    {uploadStats && (uploadStats.uploaded > 0 || uploadStats.skippedDuplicate > 0) && (
                        <div className="rounded-xl border bg-card p-5 space-y-4">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <ImageIcon className="h-4 w-4 text-primary" />
                                    {uploadStats.uploaded} photo{uploadStats.uploaded === 1 ? "" : "s"} attached
                                    <span className="font-normal text-foreground/70">
                                        to {claimAddressLabel}
                                    </span>
                                </div>
                                <div className="text-xs text-foreground/70">
                                    {uploadStats.matchedToAsset} matched · {uploadStats.attachedToSite} on the site
                                    {uploadStats.skippedDuplicate > 0 &&
                                        ` · ${uploadStats.skippedDuplicate} duplicate${
                                            uploadStats.skippedDuplicate === 1 ? "" : "s"
                                        } skipped`}
                                    {uploadStats.skippedNonImage > 0 &&
                                        ` · ${uploadStats.skippedNonImage} non-image skipped`}
                                    {uploadStats.skippedTooLarge > 0 &&
                                        ` · ${uploadStats.skippedTooLarge} too large skipped`}
                                </div>
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                                {recentUploads.slice(0, 24).map((a) => (
                                    <div
                                        key={a.id}
                                        className="relative aspect-square rounded-md overflow-hidden bg-secondary border border-border"
                                        title={`${a.fileName} → ${a.attachedTo}`}
                                    >
                                        <img
                                            src={a.previewUrl}
                                            alt={a.fileName}
                                            loading="lazy"
                                            className="absolute inset-0 h-full w-full object-cover"
                                        />
                                        {a.attachedTo === "asset" && (
                                            <span className="absolute top-1 right-1 bg-primary text-primary-foreground text-[10px] font-semibold px-1.5 py-0.5 rounded">
                                                MATCH
                                            </span>
                                        )}
                                    </div>
                                ))}
                                {recentUploads.length > 24 && (
                                    <div className="aspect-square rounded-md bg-secondary border border-border flex items-center justify-center text-xs font-medium text-foreground/70">
                                        +{recentUploads.length - 24}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Skip confirm panel — opens on the second pass of a site */}
                    {skipConfirmOpen && (
                        <div className="rounded-xl border-2 border-foreground/30 bg-card p-5 space-y-3">
                            <div className="font-semibold text-base">
                                We've skipped this address before.
                            </div>
                            <p className="text-sm text-foreground/80">
                                Are there pictures at <span className="font-bold">{claimAddressLabel}</span> in
                                ServiceTitan?
                            </p>
                            <div className="flex flex-wrap justify-end gap-2">
                                <Button variant="ghost" onClick={() => setSkipConfirmOpen(false)}>
                                    Cancel
                                </Button>
                                <Button variant="outline" onClick={() => void skipPushBack()}>
                                    <SkipForward className="h-4 w-4" />
                                    Yes — I'll come back later
                                </Button>
                                <Button variant="destructive" onClick={() => void markNoPhotos()}>
                                    <XCircle className="h-4 w-4" />
                                    No — remove from list
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Done & Next zero-photo confirm panel */}
                    {zeroDoneConfirm.open && (
                        <div className="rounded-xl border-2 border-amber-500/60 bg-amber-500/10 p-5 space-y-3">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-semibold">No photos uploaded yet.</div>
                                    <p className="text-sm text-foreground/80 mt-1">
                                        You haven't uploaded any photos for{" "}
                                        <span className="font-bold">{claimAddressLabel}</span>. Mark this site
                                        complete with 0 photos? If you didn't get to look yet, use{" "}
                                        <span className="font-medium">Skip</span> instead.
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="ghost"
                                    onClick={() => setZeroDoneConfirm({ open: false })}
                                >
                                    Cancel
                                </Button>
                                <Button onClick={() => void completeNow()}>
                                    Mark complete with 0 photos
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Action row */}
                    <div className="flex flex-wrap items-center gap-2">
                        <Button onClick={onClickDoneAndNext} size="lg" variant="success">
                            <CheckCircle2 className="h-4 w-4" />
                            Done & Next
                        </Button>
                        <Button onClick={onClickSkip} variant="outline">
                            <SkipForward className="h-4 w-4" />
                            Skip — push to back
                        </Button>
                    </div>
                </>
            )}

            {/* Re-upload bucket modal — sites where Reegen finished a
                pass but a new ServiceTitan job has happened since,
                meaning there are likely new after-photos to bring
                across. Clicking a row reclaims the queue entry and
                drops it into the main flow above. Dedup on the upload
                path silently skips bytes that are already in
                Groundwork — only NEW photos land. */}
            {reuploadOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setReuploadOpen(false);
                    }}
                >
                    <div className="bg-card rounded-xl border w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl">
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <RotateCcw className="h-5 w-5 text-amber-600" />
                                    Re-upload bucket
                                </h3>
                                <p className="text-sm text-foreground/70 mt-1">
                                    Sites you've finished but have had new ServiceTitan jobs since. Re-claim
                                    one to bring the new after-photos across — duplicates are auto-skipped.
                                </p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setReuploadOpen(false)}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="flex-1 overflow-auto p-4 space-y-2">
                            {reuploadQuery.isLoading && (
                                <div className="flex items-center justify-center py-12 text-foreground/70">
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                    Loading…
                                </div>
                            )}
                            {!reuploadQuery.isLoading && reuploadRows.length === 0 && (
                                <div className="text-center py-12 text-foreground/70">
                                    <CheckCircle2 className="h-10 w-10 mx-auto text-success mb-2" />
                                    <div className="font-semibold">Nothing to re-upload right now.</div>
                                    <div className="text-sm mt-1">
                                        New ST jobs at sites you've already finished will show up here.
                                    </div>
                                </div>
                            )}
                            {reuploadRows.map((row) => {
                                const head = [row.addressLine, row.addressLine2].filter(Boolean).join(", ");
                                const tail = [row.city, row.region, row.postalCode]
                                    .filter(Boolean)
                                    .join(", ");
                                const addr = [head, tail].filter(Boolean).join(", ") || "(no address)";
                                const uploadedAgo = row.uploadedAt
                                    ? new Date(row.uploadedAt).toLocaleDateString()
                                    : "—";
                                const jobAgo = row.lastJobCompletedAt
                                    ? new Date(row.lastJobCompletedAt).toLocaleDateString()
                                    : "—";
                                return (
                                    <div
                                        key={row.siteId}
                                        className="border rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-secondary/40"
                                    >
                                        <div className="min-w-0 flex-1">
                                            {row.customerName && (
                                                <div className="text-xs text-foreground/70 mb-0.5 truncate">
                                                    {row.customerName}
                                                </div>
                                            )}
                                            <div className="font-semibold text-sm break-words">{addr}</div>
                                            <div className="text-xs text-foreground/70 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                                <span>
                                                    Uploaded {uploadedAgo} ·{" "}
                                                    {row.priorPhotoCount.toLocaleString()} photos
                                                </span>
                                                <span>
                                                    Latest job {jobAgo}
                                                    {row.lastJobStNumber ? ` (#${row.lastJobStNumber})` : ""}
                                                </span>
                                                {row.completedJobsSinceUpload > 1 && (
                                                    <span className="text-amber-600 font-medium">
                                                        {row.completedJobsSinceUpload} jobs since upload
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            disabled={reuploading === row.siteId}
                                            onClick={() => void reclaimReupload(row.siteId)}
                                        >
                                            {reuploading === row.siteId ? (
                                                <>
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    Claiming
                                                </>
                                            ) : (
                                                <>
                                                    <RotateCcw className="h-3 w-3" />
                                                    Re-claim
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>

                        {reuploadQuery.data && reuploadQuery.data.total > reuploadRows.length && (
                            <div className="p-3 border-t text-xs text-foreground/70 text-center">
                                Showing {reuploadRows.length.toLocaleString()} of{" "}
                                {reuploadQuery.data.total.toLocaleString()}.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
